import { Tooltip } from 'antd';
import {
  SUMMARY_DATA_BASIS_LABELS,
  SUMMARY_INPUT_VIEW_CHART,
} from 'constants/uiConstants';
import type {
  SummaryDataMetric,
  SummaryInputView,
} from '../../../../../redux/slices/InsightsSlice';
import React, { useMemo } from 'react';
import { IoMdSearch } from 'react-icons/io';
import { formatCurrency } from 'utils/CurrencyUtils';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { scaleForDisplay } from '../insightUtils';
import type {
  SummaryReportRow,
  SummaryReportSideBySideSection,
} from './computeSummaryReport';
import CopyButton from './CopyButton';
import type { SummaryDataSide } from './dataBasis';
import { formatNumber } from './formatNumber';
import InputSummaryChart from './InputSummaryChart';
import SectionCard from './SectionCard';
import { buildTsv } from './summary2Utils';

const MergeIcon: React.FC<{ count: number }> = ({ count }) => (
  <Tooltip
    title={
      <>
        <div>Matching entries are summed:</div>
        <div>Combined from {count} similar items.</div>
      </>
    }
  >
    <span className="ml-1 inline-flex cursor-help align-middle">
      <IoMdSearch
        className="text-gray-muted transition-transform hover:scale-150"
        size={12}
      />
    </span>
  </Tooltip>
);

const NoQuantityDash: React.FC = () => (
  <Tooltip title="No quantity recorded for this item — its rate or unit wasn't set">
    <span className="cursor-help font-normal text-gray-muted">—</span>
  </Tooltip>
);

const AmbiguousUnitDash: React.FC<{ muted?: boolean }> = ({ muted }) => (
  <Tooltip title="Rows use different units — no single total applies">
    <span className={`cursor-help font-normal ${muted ? 'text-gray-muted' : ''}`}>
      —
    </span>
  </Tooltip>
);

interface SideTotals {
  quantityAmbiguous: boolean;
  quantityTotal: number;
  footerUnit?: string;
  footerDivisor: number;
  costTotal: number;
  costPerHaTotal: number;
}

const computeSideTotals = (
  rows: (SummaryReportRow | undefined)[]
): SideTotals => {
  const present = rows.filter((row): row is SummaryReportRow => !!row);
  const units = new Set(
    present.map((row) => row.quantityUnit).filter((u): u is string => !!u)
  );
  const quantityAmbiguous = units.size > 1;
  const uniformUnit =
    !quantityAmbiguous && units.size === 1 ? Array.from(units)[0] : undefined;
  const quantityTotal = present.reduce(
    (sum, row) => sum + (row.quantity ?? 0),
    0
  );
  const footerScale = scaleForDisplay(quantityTotal, uniformUnit);
  return {
    quantityAmbiguous,
    quantityTotal,
    footerUnit: footerScale.unit,
    footerDivisor: footerScale.unit === 't' ? 1000 : 1,
    costTotal: present.reduce((sum, row) => sum + (row.cost ?? 0), 0),
    // Footer $/ha sums each row's own cost/ha (matches GrossMarginMatrix's
    // cost-breakdown footer) rather than re-deriving a blended Σcost/Σarea —
    // rows can share the same paddock area, so a re-blended total wouldn't be
    // meaningful either way.
    costPerHaTotal: present.reduce((sum, row) => sum + (row.costPerHa ?? 0), 0),
  };
};

/** A row's quantity scaled for display (kg → t past the threshold), plus the unit it's shown in. */
const scaleRowQuantity = (row: SummaryReportRow | undefined) => {
  const scale = scaleForDisplay(row?.quantity ?? 0, row?.quantityUnit);
  return { unit: scale.unit, divisor: scale.unit === 't' ? 1000 : 1 };
};

interface SummaryReportTableProps {
  section: SummaryReportSideBySideSection;
  /** Plan, Actual, or both — both renders paired Plan / Actual columns, never a summed value. */
  sides: SummaryDataSide[];
  /** Table or chart — the page-level Input summaries view; a section never shows both. */
  view: SummaryInputView;
  /** Metric the chart plots — the page-level metric picker. */
  chartMetric: SummaryDataMetric;
}

const SummaryReportTable: React.FC<SummaryReportTableProps> = ({
  section,
  sides,
  view,
  chartMetric,
}) => {
  const { rows, hasGroup, costLabel, costPerHaLabel } = section;
  const isPaired = sides.length > 1;
  const headerRowSpan = isPaired ? 2 : undefined;
  const metricColSpan = isPaired ? sides.length : undefined;

  const totalsBySide = useMemo(
    () =>
      sides.map((side) =>
        computeSideTotals(rows.map((row) => row.sides[side]))
      ),
    [rows, sides]
  );

  const { copied, copy } = useCopyToClipboard(() => {
    const withSide = (header: string) =>
      isPaired
        ? sides.map((side) => `${header} (${SUMMARY_DATA_BASIS_LABELS[side]})`)
        : [header];
    const headers = [
      ...(hasGroup ? ['Crop', 'Variety'] : ['Item']),
      ...withSide('Quantity'),
      ...withSide('Unit'),
      ...withSide(costLabel),
      ...withSide(costPerHaLabel),
    ];
    const body = rows.map((row) => {
      const bySide = sides.map((side) => row.sides[side]);
      const scales = bySide.map(scaleRowQuantity);
      return [
        ...(hasGroup ? [row.group ?? '', row.label] : [row.label]),
        ...bySide.map((r, i) =>
          formatNumber((r?.quantity ?? 0) / scales[i].divisor)
        ),
        ...bySide.map((r, i) =>
          !r || r.quantity === null ? '' : (scales[i].unit ?? '')
        ),
        ...bySide.map((r) => formatNumber(r?.cost ?? 0)),
        ...bySide.map((r) => formatNumber(r?.costPerHa ?? 0)),
      ];
    });
    body.push([
      ...(hasGroup ? ['', 'Total'] : ['Total']),
      ...totalsBySide.map((t) =>
        t.quantityAmbiguous
          ? ''
          : formatNumber(t.quantityTotal / t.footerDivisor)
      ),
      ...totalsBySide.map((t) =>
        t.quantityAmbiguous ? '' : (t.footerUnit ?? '')
      ),
      ...totalsBySide.map((t) => formatNumber(t.costTotal)),
      ...totalsBySide.map((t) => formatNumber(t.costPerHaTotal)),
    ]);
    return buildTsv(headers, body);
  });

  const metricHeaders: { label: string; className: string }[] = [
    { label: 'Quantity', className: 'w-24 text-right' },
    { label: 'Unit', className: 'w-16 text-center' },
    { label: costLabel, className: 'w-28 text-right' },
    { label: costPerHaLabel, className: 'w-24 text-right' },
  ];

  return (
    <SectionCard
      id={`summary2-${section.id}`}
      title={section.title}
      actions={<CopyButton copied={copied} onCopy={copy} />}
    >
      {view === SUMMARY_INPUT_VIEW_CHART ? (
        <InputSummaryChart
          rows={rows}
          sides={sides}
          metric={chartMetric}
          costLabel={costLabel}
          costPerHaLabel={costPerHaLabel}
        />
      ) : (
        <div className="summary-grid-scroll w-0 min-w-full overflow-x-auto">
          <table className="w-max border-collapse text-base">
            <thead>
              <tr>
                <th
                  rowSpan={headerRowSpan}
                  className="max-w-[12rem] pb-2 pl-2 pr-6 text-left text-sm font-bold text-charcoal"
                >
                  {hasGroup ? 'Crop' : 'Item'}
                </th>
                {hasGroup && (
                  <th
                    rowSpan={headerRowSpan}
                    className="max-w-[12rem] pb-2 pr-6 text-left text-sm font-bold text-charcoal"
                  >
                    Variety
                  </th>
                  )}
                  {metricHeaders.map((header) => (
                    <th
                      key={header.label}
                      colSpan={metricColSpan}
                      className={`${isPaired ? 'text-center' : header.className} px-2 pb-2 text-sm font-bold text-charcoal`}
                    >
                      {header.label}
                    </th>
                  ))}
                </tr>
                {isPaired && (
                  <tr>
                    {metricHeaders.flatMap((header) =>
                      sides.map((side) => (
                        <th
                          key={`${header.label}-${side}`}
                          className={`${header.className} px-2 pb-2 text-xs font-semibold text-gray-dark`}
                        >
                          {SUMMARY_DATA_BASIS_LABELS[side]}
                        </th>
                      ))
                    )}
                  </tr>
                )}
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const bySide = sides.map((side) => row.sides[side]);
                  const scales = bySide.map(scaleRowQuantity);
                  const mergeCount = Math.max(
                    ...bySide.map((r) => r?.mergeCount ?? 0)
                  );
                  return (
                    <tr
                      key={hasGroup ? `${row.group}-${row.label}` : row.key}
                      className={`hover:bg-rowHover ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      <td className="max-w-[12rem] py-1.5 pl-2 pr-6 text-base text-charcoal">
                        {hasGroup ? row.group : row.label}
                        {mergeCount > 1 && <MergeIcon count={mergeCount} />}
                      </td>
                      {hasGroup && (
                        <td className="max-w-[12rem] py-1.5 pr-6 text-base text-charcoal">
                          {row.label}
                        </td>
                      )}
                      {bySide.map((r, i) => (
                        <td
                          key={`quantity-${sides[i]}`}
                          className="w-24 px-2 py-1.5 text-right tabular-nums text-charcoal"
                        >
                          {formatNumber((r?.quantity ?? 0) / scales[i].divisor)}
                        </td>
                      ))}
                      {bySide.map((r, i) => (
                        <td
                          key={`unit-${sides[i]}`}
                          className="w-16 px-2 py-1.5 text-center text-gray-muted"
                        >
                          {!r || r.quantity === null ? (
                            <NoQuantityDash />
                          ) : (
                            (scales[i].unit ?? '—')
                          )}
                        </td>
                      ))}
                      {bySide.map((r, i) => (
                        <td
                          key={`cost-${sides[i]}`}
                          className="w-28 px-2 py-1.5 text-right tabular-nums text-charcoal"
                        >
                          {formatCurrency(r?.cost ?? 0)}
                        </td>
                      ))}
                      {bySide.map((r, i) => (
                        <td
                          key={`costPerHa-${sides[i]}`}
                          className="w-24 px-2 py-1.5 text-right tabular-nums text-charcoal"
                        >
                          {formatCurrency(r?.costPerHa ?? 0)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td className="py-1.5 pl-2 pr-6 text-right text-base font-bold text-charcoal">
                    {hasGroup ? '' : 'Total:'}
                  </td>
                  {hasGroup && (
                    <td className="py-1.5 pr-6 text-right text-base font-bold text-charcoal">
                      Total:
                    </td>
                  )}
                  {totalsBySide.map((t, i) => (
                    <td
                      key={`quantity-${sides[i]}`}
                      className="w-24 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal"
                    >
                      {t.quantityAmbiguous ? (
                        <AmbiguousUnitDash muted />
                      ) : (
                        formatNumber(t.quantityTotal / t.footerDivisor)
                      )}
                    </td>
                  ))}
                  {totalsBySide.map((t, i) => (
                    <td
                      key={`unit-${sides[i]}`}
                      className="w-16 px-2 py-1.5 text-center font-bold text-gray-muted"
                    >
                      {t.quantityAmbiguous ? (
                        <AmbiguousUnitDash />
                      ) : (
                        (t.footerUnit ?? '—')
                      )}
                    </td>
                  ))}
                  {totalsBySide.map((t, i) => (
                    <td
                      key={`cost-${sides[i]}`}
                      className="w-28 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal"
                    >
                      {formatCurrency(t.costTotal)}
                    </td>
                  ))}
                  {totalsBySide.map((t, i) => (
                    <td
                      key={`costPerHa-${sides[i]}`}
                      className="w-24 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal"
                    >
                      {formatCurrency(t.costPerHaTotal)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
        </div>
      )}
    </SectionCard>
  );
};

export default SummaryReportTable;
